/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import {
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPageHeader,
  EuiSkeletonLoading,
  EuiSkeletonRectangle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import Breadcrumbs from '@modules/cui/Breadcrumbs'
import type { RoutableBreadcrumb } from '@modules/ui-types'
import { connectedClusterURL } from '@modules/ccm-lib/urls'
import type { AnyCluster, AnyMultiErrorResponse } from '@modules/ccm-lib/types'

import { PAGE_BREADCRUMBS } from '../breadcrumbs'

import { CCMClusterActionsButton } from './CCMClusterActionsButton'
import CCMClusterOverview from './CCMClusterOverview'
import CCMClusterServices from './CCMClusterServices'

import type { UseQueryResult } from 'react-query'
import type { FC } from 'react'

type Props = {
  canRenameCluster: boolean
  onClickDisconnectCluster: (clusterId: string) => void
  onClickRenameCluster: (clusterId: string, name: string) => void
  isSaving: boolean
} & UseQueryResult<AnyCluster, AnyMultiErrorResponse>

const ClusterManagementPage: FC<Props> = ({
  canRenameCluster,
  onClickDisconnectCluster,
  onClickRenameCluster,
  isSaving,
  isError: isFetchClusterError,
  error: fetchClusterError,
  isSuccess: isFetchClusterSuccess,
  isLoading: isFetchClusterLoading,
  data: ccmCluster,
}) => {
  if (isFetchClusterError) {
    return (
      <EuiEmptyPrompt
        iconType='error'
        color='danger'
        layout='vertical'
        paddingSize='l'
        title={
          <h2>
            <FormattedMessage
              id='cluster-page.error-title'
              defaultMessage='Error fetching connected cluster details'
            />
          </h2>
        }
        body={fetchClusterError.errors.map((error) => (
          <p key={error.code}>{error.message}</p>
        ))}
      />
    )
  }

  const pageBreadcrumbs: RoutableBreadcrumb[] = [...PAGE_BREADCRUMBS]

  if (isFetchClusterSuccess) {
    pageBreadcrumbs.push({
      text: ccmCluster.name,
      to: connectedClusterURL(ccmCluster.id),
    })
  }

  return (
    <Fragment>
      <Breadcrumbs breadcrumbs={pageBreadcrumbs} />

      <EuiSkeletonLoading
        isLoading={isFetchClusterLoading}
        loadingContent={<SkeletonContent />}
        loadedContent={
          isFetchClusterSuccess && (
            <EuiFlexGroup direction='column' gutterSize='l'>
              <EuiPageHeader
                bottomBorder={true}
                pageTitle={ccmCluster.name}
                rightSideItems={[
                  <CCMClusterActionsButton
                    ccmCluster={ccmCluster}
                    onClickDisconnectCluster={onClickDisconnectCluster}
                  />,
                ]}
              />
              <EuiFlexItem>
                <CCMClusterOverview
                  ccmCluster={ccmCluster}
                  canRenameCluster={canRenameCluster}
                  onClickRenameCluster={onClickRenameCluster}
                  isSaving={isSaving}
                />
              </EuiFlexItem>
              <EuiFlexItem>
                <CCMClusterServices ccmCluster={ccmCluster} />
              </EuiFlexItem>
            </EuiFlexGroup>
          )
        }
      />
    </Fragment>
  )
}

const SkeletonContent = () => (
  <EuiFlexGroup direction='column' gutterSize='l'>
    <EuiPageHeader
      bottomBorder={true}
      pageTitle={
        <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
          <EuiSkeletonRectangle width={200} height={36} />
          <EuiSkeletonRectangle width={112} height={40} />
        </EuiFlexGroup>
      }
    />
    <EuiFlexItem>
      <EuiSkeletonRectangle width={1320} height={115} />
    </EuiFlexItem>
    <EuiFlexItem>
      <EuiSkeletonRectangle width={1320} height={152} />
    </EuiFlexItem>
  </EuiFlexGroup>
)

export default ClusterManagementPage
